import { TRIAL_CREDITS } from '@aimfora/billing'
import { m } from '@aimfora/i18n'
import { Button, SkipLink } from '@aimfora/ui'
import { createFileRoute, Link } from '@tanstack/react-router'
import { LegalLinks } from '~/components/legal-page'
import { Logo } from '~/components/logo'
import { PricePlans } from '~/components/price-plans'
import { SchoolCalculator } from '~/components/school-calculator'
import { getPublicConfig } from '~/functions/session.functions'
import { LONG_SUBMISSION_PAGES, PLANS } from '~/lib/billing'
import { contactLink } from '~/lib/legal'
import { priceOffers, vatNote } from '~/lib/pricing'

export const Route = createFileRoute('/pricing')({
  loader: () => getPublicConfig(),
  head: () => ({ meta: [{ title: `${m.pricing_title()} - ${m.app_name()}` }] }),
  component: PricingPage,
})

/** The plans and prices, and what a school pays. Public: no account and no organisation needed. */
function PricingPage() {
  const { user } = Route.useRouteContext()
  const { signupMode, company } = Route.useLoaderData()
  const signedIn = Boolean(user)
  const betaClosed = signupMode !== 'open'
  const signupLabel = betaClosed ? m.forside_cta_invited() : m.forside_cta_create()

  return (
    <div className="flex min-h-screen flex-col bg-paper">
      <SkipLink>{m.common_skip_to_content()}</SkipLink>
      <header className="border-line border-b bg-surface">
        <div className="mx-auto flex h-14 max-w-6xl items-center gap-3 px-4 sm:px-6">
          <Link to="/" className="mr-auto flex items-center" aria-label={m.forside_home_aria()}>
            <Logo height={24} />
          </Link>
          {signedIn ? (
            <Button asChild variant="outline" size="sm">
              <Link to="/app">{m.forside_open_app()}</Link>
            </Button>
          ) : (
            <>
              <Button asChild variant="ghost" size="sm">
                <Link to="/login">{m.landing_login()}</Link>
              </Button>
              <Button asChild variant="outline" size="sm">
                <Link to="/signup">{m.forside_cta_create()}</Link>
              </Button>
            </>
          )}
        </div>
      </header>

      <main id="main" className="flex-1">
        <section
          aria-labelledby="pricing-plans"
          className="mx-auto max-w-6xl px-4 pt-12 pb-12 sm:px-6"
        >
          <div className="text-center">
            <h1 className="font-semibold text-[34px] leading-[1.1] tracking-[-0.02em] sm:text-[40px]">
              {m.pricing_title()}
            </h1>
            <p className="mx-auto mt-3 max-w-2xl text-[17px] text-ink-soft">{m.pricing_intro()}</p>
            <p
              className="mx-auto mt-3 max-w-2xl text-ink-soft text-sm"
              data-testid="pricing-vat-note"
            >
              {vatNote()}
            </p>
          </div>
          <h2 id="pricing-plans" className="sr-only">
            {m.pricing_plans_heading()}
          </h2>
          <PricePlans offers={priceOffers()} signedIn={signedIn} signupLabel={signupLabel} />
        </section>

        <section
          id="skole"
          aria-labelledby="pricing-school"
          className="mx-auto max-w-6xl scroll-mt-4 px-4 pb-12 sm:px-6"
        >
          <h2 id="pricing-school" className="font-semibold text-[19px]">
            {m.pricing_calc_heading()}
          </h2>
          <p className="mt-1 mb-4 max-w-prose text-ink-soft text-sm">
            {m.pricing_calc_intro({ min: String(PLANS.school.perStudent?.min ?? 1) })}
          </p>
          <div className="rounded-md border border-line bg-surface p-5">
            <SchoolCalculator contact={company.email ? contactLink(company) : null} />
          </div>
          <p className="mt-3 text-ink-soft text-sm">{m.pricing_calc_order()}</p>
        </section>

        <section className="border-line border-y bg-surface">
          <div className="mx-auto max-w-3xl px-4 py-12 sm:px-6">
            <h2 className="font-semibold text-[24px] tracking-[-0.01em]">
              {m.pricing_credits_heading()}
            </h2>
            <p className="mt-3 max-w-prose text-pretty">
              {m.pricing_credits_1({ pages: String(LONG_SUBMISSION_PAGES) })}
            </p>
            <p className="mt-2 max-w-prose text-pretty text-ink-soft">{m.pricing_credits_2()}</p>
          </div>
        </section>

        <section className="border-[#F3DDB4] border-t bg-saffron-soft">
          <div className="mx-auto max-w-6xl px-4 py-12 text-center sm:px-6">
            <h2 className="font-semibold text-[24px] tracking-[-0.01em]">
              {m.pricing_cta_title()}
            </h2>
            <p className="mx-auto mt-2 max-w-xl text-pretty text-ink-soft">
              {betaClosed
                ? m.pricing_cta_body_invited({ count: String(TRIAL_CREDITS) })
                : m.pricing_cta_body({ count: String(TRIAL_CREDITS) })}
            </p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              {signedIn ? (
                <Button asChild>
                  <Link to="/app/settings/billing">{m.pricing_cta_plan()}</Link>
                </Button>
              ) : (
                <>
                  <Button asChild>
                    <Link to="/signup">{signupLabel}</Link>
                  </Button>
                  {betaClosed && (
                    <Button asChild variant="outline">
                      <Link to="/beta">{m.forside_cta_request_beta()}</Link>
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>
        </section>
      </main>

      <footer className="border-line border-t bg-surface">
        <div className="mx-auto max-w-6xl px-4 py-6 text-[13px] text-ink-soft sm:px-6">
          <LegalLinks className="flex flex-wrap gap-x-4" />
        </div>
      </footer>
    </div>
  )
}
